/* ============================================================
   V5 测评页 —— 真后端 V5 出题接口驱动（M4 serving）
   ------------------------------------------------------------
   后端契约（apps/assessment/v5_serving.py）：
     · GET  /assessments/{id}/current-question/  逐题下发当前题
     · POST /assessments/{id}/answer/            提交单题 → 推进
   返回字段：phase/round_number/order_no/total_questions/total_rounds/
            answered_count/question{id,stem,options,dimension}
   选项脱敏（无 main_score）。AI 题预生成中返回 status='generating'，
   前端 setTimeout 2s 重拉；status='completed' 跳报告页。
   测评为纯能力轮，不再有 trait 阶段。
   ============================================================ */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getCurrentQuestion, submitAnswer } from '../api/assessment';
import EngineerPanel from '../components/EngineerPanel';
import AssessmentPreparingScreen from '../components/AssessmentPreparingScreen';
import './AssessmentPage.css';

const pad2 = (n) => String(n).padStart(2, '0');
const clock = (sec) => `${pad2(Math.floor(sec / 60))}:${pad2(sec % 60)}`;

export default function AssessmentPage() {
  const navigate = useNavigate();
  const { id: assessmentId } = useParams();
  const location = useLocation();
  const returnTo = location.pathname + location.search + location.hash;

  const [serverData, setServerData] = useState(null);    // 后端响应整包
  const [selectedLetter, setSelectedLetter] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const questionStartRef = useRef(performance.now());
  const generatingTimerRef = useRef(null);

  // ---- 拉当前题 ----
  const fetchCurrent = useCallback(async () => {
    setError('');
    try {
      const { data } = await getCurrentQuestion(assessmentId);
      if (data.status === 'completed') {
        setServerData((previous) => ({ ...previous, ...data }));
        setDone(true);
        return;
      }
      if (data.status === 'generating') {
        setServerData(data);
        if (generatingTimerRef.current) clearTimeout(generatingTimerRef.current);
        generatingTimerRef.current = setTimeout(fetchCurrent, 2000);
        return;
      }
      setServerData(data);
      setSelectedLetter('');
      questionStartRef.current = performance.now();
    } catch (err) {
      if (err.response?.data?.code === 'MANAGER_LEVEL_REQUIRED') {
        if (generatingTimerRef.current) clearTimeout(generatingTimerRef.current);
        navigate('/assessment/rules?' + new URLSearchParams({ assessment_id: assessmentId }), { replace: true });
        return;
      }
      if (err.response?.data?.code === 'PROFILE_INCOMPLETE') {
        navigate('/profile/setup?' + new URLSearchParams({ returnTo }), { replace: true });
        return;
      }
      setError(err.response?.data?.detail || '加载题目失败');
    }
  }, [assessmentId, navigate, returnTo]);

  // 首次加载 + 卸载清理
  useEffect(() => {
    fetchCurrent();
    return () => {
      if (generatingTimerRef.current) clearTimeout(generatingTimerRef.current);
    };
  }, [fetchCurrent]);

  // 计时
  useEffect(() => {
    if (done) return undefined;
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [done]);

  // ---- 提交单题 ----
  const submit = useCallback(async (special) => {
    const q = serverData?.question;
    if (!q?.id || isSubmitting) return;
    if (!special && !selectedLetter) return;
    setIsSubmitting(true);
    setError('');
    try {
      const payload = {
        question_id: q.id,
        time_spent_ms: Math.max(
          0,
          Math.round(performance.now() - questionStartRef.current),
        ),
        selected_options: special ? [] : [selectedLetter],
        special_option: special || 'none',
      };
      const { data } = await submitAnswer(assessmentId, payload);
      if (data.status === 'completed') {
        setServerData((previous) => ({ ...previous, ...data }));
        setDone(true);
        return;
      }
      await fetchCurrent();
    } catch (err) {
      // 提交请求可能已在服务端完成，只是响应因超时/断网未到达客户端。
      // 先向服务端对账，避免用户再次提交才发现测评其实已经完成。
      try {
        const { data: current } = await getCurrentQuestion(assessmentId);
        if (current.status === 'completed') {
          setServerData((previous) => ({ ...previous, ...current }));
          setDone(true);
          return;
        }
        if (current.status === 'generating') {
          setServerData(current);
          setSelectedLetter('');
          if (generatingTimerRef.current) clearTimeout(generatingTimerRef.current);
          generatingTimerRef.current = setTimeout(fetchCurrent, 2000);
          return;
        }
        if (current.status === 'ok' && current.question?.id !== q.id) {
          setServerData(current);
          setSelectedLetter('');
          questionStartRef.current = performance.now();
          return;
        }
      } catch {
        // 对账失败时保留原始提交错误，允许用户重试。
      }
      setError(err.response?.data?.detail || '提交失败，请重试');
    } finally {
      setIsSubmitting(false);
    }
  }, [assessmentId, selectedLetter, serverData, isSubmitting, fetchCurrent]);

  // 键盘：A–D 选择，Enter 提交
  useEffect(() => {
    const q = serverData?.question;
    if (done || !q || serverData?.status !== 'ok') return undefined;
    const onKey = (e) => {
      if (e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA') return;
      const k = e.key.toUpperCase();
      if (['A', 'B', 'C', 'D'].includes(k)) {
        if (q.options.find((o) => o.letter === k)) setSelectedLetter(k);
      } else if (e.key === 'Enter' && selectedLetter && !isSubmitting) {
        submit();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [serverData, selectedLetter, isSubmitting, done, submit]);

  // ---- render ----
  if (done) {
    return (
      <CompletionPage
        total={serverData?.total_questions || 0}
        elapsed={elapsed}
        onReport={() => navigate(`/report/v5/${assessmentId}`)}
        onHome={() => navigate('/')}
        learningUrl={serverData?.learning_url}
      />
    );
  }

  const q = serverData?.question;
  const isGenerating = serverData?.status === 'generating';
  const isReady = serverData?.status === 'ok' && q;
  const total = serverData?.total_questions || 0;
  const totalRounds = serverData?.total_rounds || 1;
  const answeredCount = serverData?.answered_count || 0;
  // generating 态后端不下发 order_no，回退到 answered_count（正常态二者相等），
  // 避免顶部计数在「正在生成题目」时被重置成 01。
  const orderNo = serverData?.order_no ?? serverData?.answered_count ?? 0;
  const roundNumber = serverData?.round_number || 1;
  const percent = total > 0 ? Math.round((answeredCount / total) * 100) : 0;

  return (
    <div className="v5-assessment">
      {/* ===== slim header ===== */}
      <header className="va-topbar">
        <div className="va-brand">
          <span className="va-logo">AIQ</span>
          <span className="va-brand-tag">AI 领导力测评</span>
        </div>
        <nav className="va-nav">
          <button type="button" onClick={() => navigate('/')}>模型</button>
          <button type="button" className="active">测评</button>
          <button type="button" className="disabled" disabled>报告</button>
        </nav>
        <div className="va-topbar-right">
          <span className="va-saved">进度已自动保存</span>
          <button type="button" className="va-btn ghost sm" onClick={() => navigate('/')}>
            保存并退出
          </button>
        </div>
      </header>

      {/* ===== 进度条（线性，round + 百分比） ===== */}
      <div className="va-progress">
        <div className="va-progress-inner">
          <div className="va-progress-meta">
            <span className="now">{pad2(Math.min(orderNo + 1, total))}</span>
            <span className="total">/ {total} 题</span>
          </div>
          <div className="va-segbar">
            <div className="va-seg cur" style={{ flex: 1 }}>
              <div className="va-seg-fill" style={{ width: `${percent}%` }} />
            </div>
          </div>
          <div className="va-progress-pct">{percent}%</div>
        </div>
      </div>

      {/* ===== 主体 ===== */}
      <main className="va-stage">
        {error && !isReady ? (
          <LoadingCard message={error} onRetry={fetchCurrent} />
        ) : isGenerating ? (
          <AssessmentPreparingScreen />
        ) : !isReady ? (
          <AssessmentPreparingScreen fetching />
        ) : (
          <>
            <article className="va-qcard">
              <h1 className="va-qtitle">
                {[q.scenario_text, q.stem].filter(Boolean).join(' ')}
              </h1>

              <div className="va-options">
                {q.options.map((opt) => {
                  const sel = selectedLetter === opt.letter;
                  return (
                    <button
                      type="button"
                      key={opt.letter}
                      className={`va-opt${sel ? ' selected' : ''}`}
                      onClick={() => setSelectedLetter(opt.letter)}
                      disabled={isSubmitting}
                    >
                      <span className="va-opt-key">{opt.letter}</span>
                      <span className="va-opt-text">
                        <b>{opt.text}</b>
                      </span>
                      <span className="va-opt-check" />
                    </button>
                  );
                })}
              </div>

              {error && (
                <div style={{
                  marginTop: 12, padding: '10px 14px', borderRadius: 8,
                  background: '#fdecec', color: '#b42318', fontSize: 13.5,
                }}>{error}</div>
              )}

              <div className="va-qnav">
                <div className="va-qnav-left">
                  <button type="button" className="va-btn ghost" disabled
                          title="V5 单题作答提交后不可回退">
                    ← 上一题
                  </button>
                  <button type="button" className="va-btn ghost"
                          onClick={() => submit('skipped')} disabled={isSubmitting}>
                    跳过
                  </button>
                </div>
                <div className="va-qnav-right">
                  <span className="va-hint">
                    快捷键 <kbd>A–D</kbd> 选择 · <kbd>↵</kbd> 下一题
                  </span>
                  <button
                    type="button"
                    className="va-btn primary"
                    onClick={() => submit()}
                    disabled={!selectedLetter || isSubmitting}
                  >
                    {answeredCount + 1 >= total ? '完成测评 →' : '下一题 →'}
                  </button>
                </div>
              </div>
            </article>

            {/* 侧栏 */}
            <aside className="va-side">
              <div className="va-side-card">
                <h5>
                  本次进度
                  <span className="mono">{pad2(Math.min(orderNo + 1, total))} / {total}</span>
                </h5>
                <div className="va-navmap">
                  {Array.from({ length: total }).map((_, i) => {
                    let cls = 'todo';
                    if (i < answeredCount) cls = 'done';
                    else if (i === orderNo) cls = 'cur';
                    return (
                      <span
                        key={i}
                        className={`va-nm-cell ${cls}`}
                        title={`第 ${i + 1} 题`}
                      >
                        {pad2(i + 1)}
                      </span>
                    );
                  })}
                </div>
                <div className="va-nm-legend">
                  <div className="row"><span className="sw cur" />当前</div>
                  <div className="row"><span className="sw done" />已完成</div>
                  <div className="row"><span className="sw todo" />未作答</div>
                </div>
              </div>

              <div className="va-side-card">
                <h5>本次会话 <span className="mono">SESSION</span></h5>
                <div className="va-meta-list">
                  <div className="it"><span className="k">已用时间</span><span className="v">{clock(elapsed)}</span></div>
                  <div className="it"><span className="k">阶段</span>
                    <span className="v">能力测评</span>
                  </div>
                  <div className="it"><span className="k">当前轮</span>
                    <span className="v">{roundNumber} / {totalRounds}</span>
                  </div>
                </div>
                <p className="va-side-note">
                  {`能力测评共 ${totalRounds} 轮，每轮 ${totalRounds ? Math.round(total / totalRounds) : 5} 题（五力各 1），共 ${total} 题；可随时退出，进度自动保存。`}
                </p>
              </div>

              {/* 工程模式：后端按 User.is_engineer_mode 权威下发 engineer_info（普通用户拿不到）；
                  前端只按字段是否存在渲染，避免 AuthContext 旧快照漏判（开了工程模式仍不显示）。 */}
              {q?.engineer_info && (
                <EngineerPanel info={q.engineer_info} />
              )}
            </aside>
          </>
        )}
      </main>
    </div>
  );
}

function LoadingCard({ message, onRetry }) {
  return (
    <div className="va-transition">
      <div className="va-transition-card">
        <div className="va-transition-kicker">LOADING</div>
        <h2 style={{ fontSize: 22 }}>{message}</h2>
        {onRetry && (
          <button type="button" className="va-btn primary lg" onClick={onRetry}
                  style={{ marginTop: 12 }}>
            重试
          </button>
        )}
      </div>
    </div>
  );
}

function CompletionPage({ total, elapsed, onReport, onHome, learningUrl }) {
  return (
    <div className="v5-assessment">
      <main className="va-stage">
        <div className="va-transition">
          <div className="va-transition-card">
            <div className="va-transition-mark done">★</div>
            <div className="va-transition-kicker">ASSESSMENT COMPLETE</div>
            <h2>测评完成</h2>
            <p>你已完成全部 {total} 道题目，报告将在后台异步生成（通常 1-2 分钟）。</p>
            <div className="va-complete-stats">
              <div className="stat"><span className="num">{total}</span><span className="lab">题目总数</span></div>
              <div className="stat"><span className="num">{clock(elapsed)}</span><span className="lab">总用时</span></div>
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              {learningUrl && <a className="va-btn primary lg" href={learningUrl}>返回学习</a>}
              <button type="button" className="va-btn primary lg" onClick={onReport}>
                查看报告 →
              </button>
              <button type="button" className="va-btn ghost lg" onClick={onHome}>
                返回首页
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
